/*
 * ---------------------------------------------------------------
 * ## HAND-WRITTEN STAND-IN FOR A SWAGGER-GENERATED CLIENT       ##
 * ##                                                           ##
 * ## The global-search endpoints are not on a deployed backend  ##
 * ## yet, so `yarn generate:api-*` cannot emit this class. It   ##
 * ## mirrors the generated shape exactly (extends HttpClient,   ##
 * ## `this.request`, `secure: true`) and lives outside          ##
 * ## `src/api/` only because that folder is git-ignored and     ##
 * ## wiped on every regeneration. Once the backend ships,       ##
 * ## delete this file and re-point the import at `@/api/Search`.##
 * ---------------------------------------------------------------
 */
import { ContentType, HttpClient, RequestParams } from '@/api/http-client';

import {
  SearchByCategoryRequest,
  SearchFrame,
  SearchHitResponse,
  SearchRequest,
} from '../model/types';

export interface PageResponseSearchHitResponse {
  content: SearchHitResponse[];
  totalElements: number;
}

export interface SearchStreamParams {
  /** Bearer token for the streamed request. Axios cannot stream a response body in
   *  the browser, so the NDJSON call goes out through `fetch` and carries its own
   *  Authorization header instead of inheriting the axios instance's. */
  token: string | undefined;
  signal: AbortSignal;
  onFrame: (frame: SearchFrame) => void;
}

export class SearchStreamRejectedError extends Error {
  constructor() {
    super('Search rejected the request: the load-shedding bulkhead is full.');
    this.name = 'SearchStreamRejectedError';
  }
}

export class Search<SecurityDataType = unknown> extends HttpClient<SecurityDataType> {
  /**
   * @description Grouped, streamed global search. Emits one NDJSON frame per line as each
   * category lane finishes, so results paint progressively instead of waiting for the
   * slowest lane.
   *
   * @tags search-controller
   * @name Search
   * @request POST:/search
   * @secure
   * @response `200` `void` OK — an `application/x-ndjson` stream of SearchFrame lines
   * @response `429` `void` Too Many Requests — the search bulkhead is full
   */
  search = async (data: SearchRequest, { token, signal, onFrame }: SearchStreamParams) => {
    const response = await fetch(`${this.streamBaseUrl()}/search`, {
      method: 'POST',
      headers: {
        'Content-Type': ContentType.Json,
        Accept: 'application/x-ndjson',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(data),
      signal,
    });

    if (response.status === 429) throw new SearchStreamRejectedError();
    if (!response.ok || !response.body) {
      throw new Error(`${response.status} ${response.statusText}`);
    }

    await readNdjson(response.body, onFrame);
  };

  /**
   * @description Drill-in for a single category: plain JSON, fully paged.
   *
   * @tags search-controller
   * @name SearchByCategory
   * @request POST:/search/byCategory
   * @secure
   * @response `200` `PageResponseSearchHitResponse` OK
   */
  searchByCategory = (data: SearchByCategoryRequest, params: RequestParams = {}) =>
    this.request<PageResponseSearchHitResponse, void>({
      path: `/search/byCategory`,
      method: 'POST',
      body: data,
      secure: true,
      type: ContentType.Json,
      format: 'json',
      ...params,
    });

  /** Taken from the axios instance so the stream always hits the same origin the
   *  generated client was built against — there is no second source of truth. */
  private streamBaseUrl = () => (this.instance.defaults.baseURL ?? '').replace(/\/$/, '');
}

/** A chunk can split a line anywhere, so the tail is held back until its newline
 *  arrives. The trailing fragment is flushed once the body closes. */
async function readNdjson(body: ReadableStream<Uint8Array>, onFrame: (frame: SearchFrame) => void) {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  const emit = (line: string) => {
    if (!line.trim()) return;
    try {
      onFrame(JSON.parse(line) as SearchFrame);
    } catch {
      // A truncated or non-JSON line is not fatal: the census in the `done` frame is
      // what tells the caller whether the stream actually completed.
    }
  };

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';
      lines.forEach(emit);
    }
    emit(buffer);
  } finally {
    reader.cancel().catch(() => undefined);
  }
}
